import { proxy } from 'valtio';

// ─── Token key mapping ──────────────────────────────────────────────────────
// Each role uses its own storage key so tokens never collide.
export const TOKEN_KEYS = {
  admin:           'admin_token',
  super_admin:     'admin_token',
  superadmin:      'admin_token',
  serviceProvider: 'assistant_token',
  assistant:       'assistant_token',
  user:            'user_token',
};
const DEFAULT_TOKEN_KEY = 'user_token';

// ─── Tab session scope ──────────────────────────────────────────────────────
// Which role this tab is signed in as. sessionStorage is private to a tab, so a
// user, an assistant and an admin can each hold a session in their own tab.
// localStorage is shared by every tab, so nothing tab-specific may be decided
// from it alone — doing so is what let one tab's login take over the others.
const TAB_SCOPE_KEY = 'auth_tab_scope';
const SCOPES = ['user', 'assistant', 'admin'];
// Pinned on logout so a signed-out tab stays signed out, instead of adopting a
// session another tab remembered for a different role.
const SIGNED_OUT = 'signed_out';

export function getTokenKey(role) {
  if (!role) return DEFAULT_TOKEN_KEY;
  const normalized = String(role).trim().toLowerCase();
  // Check direct match first
  if (TOKEN_KEYS[role]) return TOKEN_KEYS[role];
  // Admin variants
  if (normalized.includes('admin')) return 'admin_token';
  // Assistant variants
  if (normalized === 'serviceprovider' || normalized === 'service_provider')
    return 'assistant_token';
  return DEFAULT_TOKEN_KEY;
}

/** Maps any role name the app uses onto one of the three session scopes. */
export function getScopeForRole(role) {
  const key = getTokenKey(role);
  if (key === 'admin_token') return 'admin';
  if (key === 'assistant_token') return 'assistant';
  return 'user';
}

/**
 * Read the stored token for a role key. The tab's own sessionStorage wins over
 * a remembered localStorage copy, so a tab-only sign-in is never shadowed by an
 * older remembered session of the same role.
 */
export function getStoredTokenByKey(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || null;
}

const inferScopeFromPath = () => {
  const path = window.location?.pathname || '';
  if (path.startsWith('/admin')) return 'admin';
  if (
    path.startsWith('/dashboards/assistant') ||
    /^\/assistant\/(kyc|experience|documents|vehicle|pricing|profile)/.test(path)
  ) {
    return 'assistant';
  }
  return null;
};

/**
 * The role this tab is signed in as. A tab that has not signed in yet — a fresh
 * tab on a remembered session — adopts the session its URL belongs to, falling
 * back to whichever session exists.
 */
export function getTabScope() {
  const pinned = sessionStorage.getItem(TAB_SCOPE_KEY);
  if (pinned === SIGNED_OUT) return null;
  if (SCOPES.includes(pinned)) return pinned;

  const fromPath = inferScopeFromPath();
  if (fromPath && getStoredTokenByKey(getTokenKey(fromPath))) return fromPath;

  return SCOPES.find((scope) => getStoredTokenByKey(getTokenKey(scope))) || null;
}

/** The token belonging to this tab's session — never another tab's. */
export function getTabToken() {
  const scope = getTabScope();
  return scope ? getStoredTokenByKey(getTokenKey(scope)) : null;
}

/** Cold boot: pick up this tab's session and pin the tab to it. */
function readStoredToken() {
  const scope = getTabScope();
  if (!scope) return null;
  const token = getStoredTokenByKey(getTokenKey(scope));
  if (token) sessionStorage.setItem(TAB_SCOPE_KEY, scope);
  return token;
}

/** The scope this tab's session is known to be, pinned or hydrated. */
function currentScope() {
  const pinned = sessionStorage.getItem(TAB_SCOPE_KEY);
  if (SCOPES.includes(pinned)) return pinned;
  return authStore.role ? getScopeForRole(authStore.role) : null;
}

export const authStore = proxy({
  user:      null,
  token:     readStoredToken(),
  role:      null,
  isLoading: false,
  isError:   false,
  error:     null,

  /**
   * Save the access token and pin this tab to the role it belongs to.
   * @param {string}  token
   * @param {boolean} rememberMe – true → localStorage, false → sessionStorage
   * @param {string}  [role]     – role (or scope) used to pick the key
   */
  setToken(token, rememberMe = false, role) {
    authStore.token = token;
    const scope = getScopeForRole(role ?? authStore.role ?? getTabScope());
    const key = getTokenKey(scope);

    if (token) {
      const previousScope = sessionStorage.getItem(TAB_SCOPE_KEY);
      // Signing in as a different role in this tab: drop the old role's
      // tab-only token. A remembered copy may still be in use by another tab.
      if (previousScope && previousScope !== scope) {
        sessionStorage.removeItem(getTokenKey(previousScope));
      }
      sessionStorage.setItem(TAB_SCOPE_KEY, scope);

      if (rememberMe) {
        localStorage.setItem(key, token);
        // A leftover tab-only copy would shadow the remembered one.
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, token);
      }
    } else {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  },

  setUser(user) {
    authStore.user = user;
    if (user) {
      authStore.role = user.role;
    } else {
      authStore.role = null;
    }
  },

  setRole(role) {
    authStore.role = role;
  },

  setLoading(loading) {
    authStore.isLoading = loading;
  },

  setError(error) {
    authStore.error = error;
    authStore.isError = !!error;
  },

  /** Ends this tab's session only. Other tabs' roles are left signed in. */
  reset() {
    const scope = currentScope();
    if (scope) {
      const key = getTokenKey(scope);
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
    sessionStorage.setItem(TAB_SCOPE_KEY, SIGNED_OUT);

    authStore.user  = null;
    authStore.token = null;
    authStore.role  = null;
    authStore.isLoading = false;
    authStore.isError   = false;
    authStore.error     = null;
  },
});

export async function performLogout() {
  const scope = currentScope();
  // No scope means this tab never held a session, so there is nothing to end on
  // the server — and calling logout anyway would end another tab's session.
  if (scope) {
    try {
      const axiosInstance = require('../api/Axiosinstance').default;
      await axiosInstance.post('/auth/logout', { scope });
    } catch {
      // Always clear client state regardless of server response
    }
  }
  authStore.reset();
}
